// src/pages/Groups.tsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Paper, Button, Box, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Collapse, Tooltip, Divider, Checkbox,
} from '@mui/material';
import {
  Add, Delete, Group as GroupIcon, ExpandMore, ExpandLess,
  ArrowForward, ArrowBack, Search,
} from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';

interface Account {
  id: string;
  name: string;
  phone_number: string;
  status: 'AUTHORIZED' | 'INACTIVE';
}

interface AccountGroup {
  id: string;
  name: string;
  linked_accounts: Account[];
}

// ─── Отдельный компонент PanelList (исправлено) ─────────────────────────────
const PanelList: React.FC<{
  items: Account[];
  checked: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  search: string;
  onSearch: (v: string) => void;
  emptyText: string;
  color: string;
}> = ({ items, checked, onToggle, onSelectAll, search, onSearch, emptyText, color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Поиск..."
        value={search}
        onChange={e => onSearch(e.target.value)}
        InputProps={{
          startAdornment: <Search sx={{ color: '#9CA3AF', mr: 0.5, fontSize: 18 }} />
        }}
      />
      {items.length > 0 && (
        <Button
          size="small"
          variant="text"
          onClick={onSelectAll}
          sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.75rem', color }}
        >
          Все ({items.length})
        </Button>
      )}
    </Box>

    <Box sx={{ overflowY: 'auto', flex: 1, border: '1px solid #E5E7EB', borderRadius: 1 }}>
      {items.length === 0 ? (
        <Typography sx={{ p: 3, color: 'text.secondary', textAlign: 'center', fontSize: '0.875rem' }}>
          {emptyText}
        </Typography>
      ) : (
        items.map(acc => (
          <Box
            key={acc.id}
            onClick={() => onToggle(acc.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.75,
              cursor: 'pointer',
              borderBottom: '1px solid #F3F4F6',
              bgcolor: checked.includes(acc.id) ? '#EFF6FF' : 'white',
              '&:hover': {
                bgcolor: checked.includes(acc.id) ? '#DBEAFE' : '#F9FAFB'
              },
            }}
          >
            <Checkbox
              size="small"
              checked={checked.includes(acc.id)}
              sx={{ p: 0.5, mr: 1 }}
            />
            <Box sx={{ overflow: 'hidden', flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={checked.includes(acc.id) ? 600 : 400}
                noWrap
              >
                {acc.name}
              </Typography>
              {acc.phone_number && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {acc.phone_number}
                </Typography>
              )}
            </Box>
          </Box>
        ))
      )}
    </Box>
  </Box>
);

// ─── Двухпанельный редактор состава группы ───────────────────────────────────
const GroupMemberEditor: React.FC<{
  allAccounts: Account[];
  group: AccountGroup;
  onSave: (toLink: string[], toUnlink: string[]) => void;
  isLoading: boolean;
}> = ({ allAccounts, group, onSave, isLoading }) => {
  const linkedIds = group.linked_accounts.map(a => a.id);

  const [memberIds, setMemberIds] = useState<string[]>(linkedIds);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [checkedLeft, setCheckedLeft] = useState<string[]>([]);
  const [checkedRight, setCheckedRight] = useState<string[]>([]);

  const outside = allAccounts.filter(a =>
    a.status === 'AUTHORIZED' && !memberIds.includes(a.id)
  );
  const inside = allAccounts.filter(a => memberIds.includes(a.id));

  const filteredLeft = outside.filter(a =>
    a.name.toLowerCase().includes(searchLeft.toLowerCase()) ||
    a.phone_number?.includes(searchLeft)
  );
  const filteredRight = inside.filter(a =>
    a.name.toLowerCase().includes(searchRight.toLowerCase()) ||
    a.phone_number?.includes(searchRight)
  );

  const toggleCheck = (id: string, side: 'left' | 'right') => {
    if (side === 'left') {
      setCheckedLeft(prev =>
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    } else {
      setCheckedRight(prev =>
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    }
  };

  const moveToGroup = () => {
    setMemberIds(prev => {
      const toAdd = checkedLeft.filter(id => !prev.includes(id));
      return [...prev, ...toAdd];
    });
    setCheckedLeft([]);
  };

  const removeFromGroup = () => {
    setMemberIds(prev => prev.filter(id => !checkedRight.includes(id)));
    setCheckedRight([]);
  };

  const selectAllLeft = () => setCheckedLeft(filteredLeft.map(a => a.id));
  const selectAllRight = () => setCheckedRight(filteredRight.map(a => a.id));

  const handleSave = () => {
    const toLink = memberIds.filter(id => !linkedIds.includes(id));
    const toUnlink = linkedIds.filter(id => !memberIds.includes(id));
    onSave(toLink, toUnlink);
  };

  const hasChanges =
    memberIds.some(id => !linkedIds.includes(id)) ||
    linkedIds.some(id => !memberIds.includes(id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Двухпанельный лейаут */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, flex: 1, minHeight: 0 }}>

        {/* Левая панель — доступные */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Доступные аккаунты ({outside.length})
            </Typography>
            {checkedLeft.length > 0 && (
              <Typography variant="caption" sx={{ color: '#1E40AF', fontWeight: 600 }}>
                {checkedLeft.length} отмечено
              </Typography>
            )}
          </Box>
          <PanelList
            items={filteredLeft}
            checked={checkedLeft}
            onToggle={id => toggleCheck(id, 'left')}
            onSelectAll={selectAllLeft}
            search={searchLeft}
            onSearch={setSearchLeft}
            emptyText="Все аккаунты уже в группе"
            color="#1E40AF"
          />
        </Box>

        {/* Кнопки переноса */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5, pt: 5 }}>
          <Tooltip title={checkedLeft.length ? `Добавить ${checkedLeft.length} в группу` : 'Отметьте аккаунты слева'}>
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={moveToGroup}
                disabled={checkedLeft.length === 0}
                sx={{ minWidth: 40, px: 1, bgcolor: '#1E40AF', '&:hover': { bgcolor: '#1E3A8A' } }}
              >
                <ArrowForward fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={checkedRight.length ? `Убрать ${checkedRight.length} из группы` : 'Отметьте аккаунты справа'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={removeFromGroup}
                disabled={checkedRight.length === 0}
                sx={{ minWidth: 40, px: 1 }}
              >
                <ArrowBack fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Правая панель — состав группы */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              В группе ({inside.length})
            </Typography>
            {checkedRight.length > 0 && (
              <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 600 }}>
                {checkedRight.length} отмечено
              </Typography>
            )}
          </Box>
          <PanelList
            items={filteredRight}
            checked={checkedRight}
            onToggle={id => toggleCheck(id, 'right')}
            onSelectAll={selectAllRight}
            search={searchRight}
            onSearch={setSearchRight}
            emptyText="Группа пуста"
            color="#DC2626"
          />
        </Box>
      </Box>

      {/* Итог и кнопка сохранения */}
      <Box sx={{ pt: 2, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {hasChanges ? (
            <>
              {memberIds.filter(id => !linkedIds.includes(id)).length > 0 && (
                <span style={{ color: '#16A34A' }}>
                  +{memberIds.filter(id => !linkedIds.includes(id)).length} добавится&nbsp;&nbsp;
                </span>
              )}
              {linkedIds.filter(id => !memberIds.includes(id)).length > 0 && (
                <span style={{ color: '#DC2626' }}>
                  −{linkedIds.filter(id => !memberIds.includes(id)).length} уберётся
                </span>
              )}
            </>
          ) : (
            <span>Изменений нет</span>
          )}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || !hasChanges}
          sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Сохранить изменения'}
        </Button>
      </Box>
    </Box>
  );
};

// ─── Основной компонент ───────────────────────────────────────────────────────
const Groups: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Создание
  const [openCreate, setOpenCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Редактирование
  const [editingGroup, setEditingGroup] = useState<AccountGroup | null>(null);

  // Раскрытые строки таблицы
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [auth?.isAuthenticated]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [groupsRes, accRes] = await Promise.all([
        api.get<AccountGroup[]>('/account_groups'),
        api.get<{ accounts: Account[] }>('/accounts'),
      ]);
      setGroups(groupsRes.data || []);
      setAllAccounts(accRes.data.accounts || []);
    } catch (err: any) {
      setMessage({ text: `Ошибка загрузки: ${err.response?.data?.detail || err.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    setIsLoading(true);
    try {
      await api.post('/account_groups/create', { name: newGroupName.trim() });
      setMessage({ text: 'Группа создана!', type: 'success' });
      setOpenCreate(false);
      setNewGroupName('');
      fetchData();
    } catch (err: any) {
      setMessage({ text: `Ошибка: ${err.response?.data?.detail || err.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const saveGroupAccounts = async (toLink: string[], toUnlink: string[]) => {
    if (!editingGroup) return;
    setIsLoading(true);
    try {
      if (toLink.length > 0) {
        await api.post('/account_groups/link_accounts', {
          account_ids: toLink,
          group_id: editingGroup.id,
        });
      }
      if (toUnlink.length > 0) {
        await api.post('/account_groups/unlink_accounts', {
          account_ids: toUnlink,
          group_id: editingGroup.id,
        });
      }
      setMessage({ text: 'Состав группы обновлён!', type: 'success' });
      setEditingGroup(null);
      fetchData();
    } catch (err: any) {
      setMessage({ text: `Ошибка: ${err.response?.data?.detail || err.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGroup = async (id: string) => {
    if (!window.confirm('Удалить группу? Аккаунты останутся, но будут отвязаны.')) return;
    setIsLoading(true);
    try {
      await api.delete(`/account_groups/${id}`);
      setMessage({ text: 'Группа удалена!', type: 'success' });
      fetchData();
    } catch (err: any) {
      setMessage({ text: `Ошибка: ${err.response?.data?.detail || err.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* Информационный блок */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работать с группами аккаунтов?
        </Typography>

        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Группы позволяют объединять аккаунты для быстрого выбора в инструментах.
          Это удобно, если вы работаете с разными наборами аккаунтов — например,
          для рекламы, прогрева или тестирования.
        </Typography>

        <Typography variant="subtitle1" sx={{ color: '#1E40AF', fontWeight: 600, mb: 2 }}>
          Создание группы
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
            • Нажмите <strong>«Создать группу»</strong><br />
            • Введите название (например: <em>Реклама</em>, <em>Прогрев</em>)<br />
            • Сохраните группу
          </Typography>
        </Box>

        <Typography variant="subtitle1" sx={{ color: '#1E40AF', fontWeight: 600, mb: 2 }}>
          Управление составом
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
            • Нажмите <strong>«Состав группы»</strong><br />
            • Слева — доступные аккаунты<br />
            • Справа — аккаунты в группе<br />
            • Используйте стрелки для переноса<br />
            • Нажмите <strong>«Сохранить изменения»</strong>
          </Typography>
        </Box>

        <Typography variant="subtitle1" sx={{ color: '#EF4444', fontWeight: 600, mb: 2 }}>
          Важно
        </Typography>

        <Box>
          <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
            • Один аккаунт может быть в нескольких группах<br />
            • Удаление группы не удаляет аккаунты<br />
            • Изменения применяются только после сохранения
          </Typography>
        </Box>

        <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
          Используйте группы для ускорения работы с инструментами сервиса.
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Заголовок */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
              Группы аккаунтов
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Объединяйте аккаунты для быстрого выбора в инструментах
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenCreate(true)}
            disabled={isLoading}
            sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
          >
            Создать группу
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {isLoading && groups.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress />
              <Typography mt={2}>Загрузка...</Typography>
            </Box>
          ) : groups.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A', width: 32 }} />
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Название</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Аккаунтов</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map(group => (
                    <React.Fragment key={group.id}>
                      <TableRow hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' } }}>
                        <TableCell sx={{ p: 1 }} onClick={() => toggleRow(group.id)}>
                          <IconButton size="small">
                            {expandedRows.includes(group.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        </TableCell>

                        <TableCell onClick={() => toggleRow(group.id)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupIcon fontSize="small" color="primary" />
                            <Typography fontWeight={600}>{group.name}</Typography>
                          </Box>
                        </TableCell>

                        <TableCell onClick={() => toggleRow(group.id)}>
                          <Chip
                            label={`${group.linked_accounts.length} акк.`}
                            size="small"
                            color={group.linked_accounts.length > 0 ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Редактировать состав" placement='left-end'>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setEditingGroup(group)}
                              disabled={isLoading}
                              sx={{ bgcolor: '#1E3A8A', color: 'white', '&:hover': { bgcolor: '#1E40AF' } }}
                            >
                              Состав группы
                            </Button>
                            </Tooltip>
                          <Tooltip title="Удалить группу" placement='right-end'>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteGroup(group.id)}
                              disabled={isLoading}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Раскрытая строка — чипы аккаунтов */}
                      <TableRow>
                        <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expandedRows.includes(group.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 4, py: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E5E7EB' }}>
                              {group.linked_accounts.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  Группа пуста — нажмите «Состав» чтобы добавить аккаунты
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {group.linked_accounts.map(acc => (
                                    <Chip
                                      key={acc.id}
                                      label={acc.name}
                                      size="small"
                                      variant="outlined"
                                      color={acc.status === 'AUTHORIZED' ? 'success' : 'default'}
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <GroupIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">Групп пока нет</Typography>
              <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                Создайте группу, чтобы объединять аккаунты
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenCreate(true)}
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
              >
                Создать первую группу
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* МОДАЛКА СОЗДАНИЯ */}
      <Dialog open={openCreate} onClose={() => !isLoading && setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 600 }}>Новая группа</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Название группы"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createGroup()}
            fullWidth
            placeholder="Реклама 2025, Прогрев, Основные..."
            helperText="До 50 символов"
            inputProps={{ maxLength: 50 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)} disabled={isLoading}>Отмена</Button>
          <Button
            onClick={createGroup}
            variant="contained"
            disabled={isLoading || !newGroupName.trim()}
            sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
          >
            {isLoading ? <CircularProgress size={18} color="inherit" /> : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* МОДАЛКА РЕДАКТИРОВАНИЯ СОСТАВА */}
      <Dialog
        open={!!editingGroup}
        onClose={() => !isLoading && setEditingGroup(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '85vw',
            height: '80vh',
            maxWidth: 'none',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 600, pb: 1 }}>
          Состав группы: <span style={{ color: '#1E40AF' }}>{editingGroup?.name}</span>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
            Отмечайте аккаунты и переносите стрелками. Изменения применяются одной кнопкой.
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, pt: 2 }}>
          {editingGroup && (
            <GroupMemberEditor
              allAccounts={allAccounts}
              group={editingGroup}
              onSave={saveGroupAccounts}
              isLoading={isLoading}
            />
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setEditingGroup(null)} disabled={isLoading}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Groups;